/* Seats Page
 * Handles both the seats list (/seats/) and individual seat detail (/seats/CODE/).
 * Detail view includes current vote-share bars and a Chart.js time-series line chart.
 * Loads Chart.js from CDN on demand (detail view only).
 * URL routing is handled by a Caddy rewrite: /seats/CODE → /seats/ at Ghost,
 * while the browser retains the full path for SEO.
 */
(function () {
    var API_BASE    = 'https://pollingreport.uk/api/v1';
    var CHARTJS_CDN = 'https://cdn.jsdelivr.net/npm/chart.js@4/dist/chart.umd.min.js';

    var _rootStyle = getComputedStyle(document.documentElement);
    var partyColors = {
        reform: _rootStyle.getPropertyValue('--color-party-reform').trim() || '#12b6cf',
        lab:    _rootStyle.getPropertyValue('--color-party-lab').trim() || '#e4003b',
        con:    _rootStyle.getPropertyValue('--color-party-con').trim() || '#0087dc',
        lib:    _rootStyle.getPropertyValue('--color-party-lib').trim() || '#faa61a',
        snp:    _rootStyle.getPropertyValue('--color-party-snp').trim() || '#fdf38e',
        green:  _rootStyle.getPropertyValue('--color-party-green').trim() || '#02a95b',
        plaid:  _rootStyle.getPropertyValue('--color-party-plaid').trim() || '#3f8428',
        other:  '#9E9E9E'
    };

    var partyNames = {
        con:    'Conservative',
        lab:    'Labour',
        lib:    'Liberal Democrat',
        snp:    'SNP',
        green:  'Green',
        reform: 'Reform',
        plaid:  'Plaid Cymru',
        alba:   'Alba',
        other:  'Other'
    };

    var partyOrder = ['lab', 'con', 'reform', 'lib', 'snp', 'green', 'plaid', 'other'];

    /* ------------------------------------------------------------------ */

    var root = document.querySelector('[data-ukpr-seats-page]');
    if (!root) return;

    /* Detect if we're on a detail URL: /seats/W07000081/ */
    var pathMatch = window.location.pathname.match(/^\/seats\/([A-Za-z0-9_%-]+)\/?$/i);
    var seatCode  = pathMatch ? decodeURIComponent(pathMatch[1]).trim().toUpperCase() : null;

    var listView   = root.querySelector('[data-seats-list-view]');
    var detailView = root.querySelector('[data-seats-detail-view]');

    if (seatCode) {
        listView.hidden   = true;
        detailView.hidden = false;
        /* Race bar lives outside this widget in seats.hbs — hide it on detail pages */
        var raceBar = document.querySelector('[data-seats-race-bar]');
        if (raceBar) raceBar.hidden = true;
        /* Fix canonical and og:url for seat detail pages (Caddy rewrites /seats/CODE → /seats/) */
        var canonicalUrl = window.location.origin + '/seats/' + seatCode;
        var canonicalEl = document.querySelector('link[rel="canonical"]');
        if (canonicalEl) canonicalEl.href = canonicalUrl;
        var ogUrlEl = document.querySelector('meta[property="og:url"]');
        if (ogUrlEl) ogUrlEl.content = canonicalUrl;
        runDetail();
    } else {
        listView.hidden   = false;
        detailView.hidden = true;
        runList();
    }

    /* ================================================================
       LIST VIEW
    ================================================================ */

    function runList() {
        /* Use the shared results-history model selector (chart above the table) */
        var rhModelSel      = document.querySelector('[data-rh-model]');
        var currentModel    = (rhModelSel && rhModelSel.value) ? rhModelSel.value : 'uniform';
        var searchInput     = root.querySelector('[data-seats-search-input]');
        var filterToggle    = root.querySelector('[data-seats-filter-toggle]');
        var filtersPanel    = root.querySelector('[data-seats-filters]');
        var filterIncumbent = root.querySelector('[data-filter-incumbent]');
        var filterWinner    = root.querySelector('[data-filter-winner]');
        var filterRegion    = root.querySelector('[data-filter-region]');
        var filterFlipped   = root.querySelector('[data-filter-flipped]');
        var listEl          = root.querySelector('[data-seats-list]');
        var loadingBar      = root.querySelector('[data-seats-loading]');
        var pageMeta        = root.querySelector('[data-page-meta]');
        var prevBtn         = root.querySelector('[data-page-prev]');
        var nextBtn         = root.querySelector('[data-page-next]');

        var state = { page: 1, meta: null, searchTimer: null };

        function setLoading(on) { loadingBar.hidden = !on; }

        function escHtml(s) {
            return String(s || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
        }

        function buildUrl() {
            var u = API_BASE + '/seats?paginate=true&per_page=20&verbose=true';
            u += '&model=' + encodeURIComponent(currentModel);
            u += '&page=' + state.page;
            var s = searchInput.value.trim();
            if (s)  u += '&search='    + encodeURIComponent(s);
            if (filterIncumbent.value) u += '&incumbent=' + encodeURIComponent(filterIncumbent.value);
            if (filterWinner.value)    u += '&winner='    + encodeURIComponent(filterWinner.value);
            if (filterRegion.value)    u += '&region='    + encodeURIComponent(filterRegion.value);
            if (filterFlipped.value !== '') u += '&flipped=' + encodeURIComponent(filterFlipped.value);
            return u;
        }

        function renderSeats(data) {
            if (!data || !data.length) {
                listEl.innerHTML = '<div class="ukpr-seats-empty">No seats found.</div>';
                return;
            }
            listEl.innerHTML = data.map(function (seat) {
                var winner    = String(seat.winner || '').toLowerCase();
                var label     = (partyNames[winner] || winner).toUpperCase();
                var status    = seat.flipped ? 'Gain' : 'Hold';
                var mp        = seat.constituency && seat.constituency.mp && seat.constituency.mp.full_name
                    ? seat.constituency.mp.full_name : 'Vacant';
                return '<a href="/seats/' + encodeURIComponent(seat.constituency_code.toUpperCase()) + '" class="ukpr-seat-row">'
                    + '<div>'
                    + '<div class="ukpr-seat-row-name">'  + escHtml(seat.constituency_name) + '</div>'
                    + '<div class="ukpr-seat-row-mp">'    + escHtml(mp) + '</div>'
                    + '</div>'
                    + '<div class="ukpr-seat-row-winner party-' + escHtml(winner) + '">'
                    + escHtml(label) + ' ' + escHtml(status)
                    + '</div>'
                    + '</a>';
            }).join('');
        }

        function updatePagination() {
            if (!state.meta) { pageMeta.textContent = ''; return; }
            pageMeta.textContent = 'Page ' + state.meta.current_page + ' of ' + state.meta.last_page;
            prevBtn.disabled = (state.page <= 1);
            nextBtn.disabled = (state.page >= state.meta.last_page);
        }

        function fetchSeats() {
            setLoading(true);
            fetch(buildUrl())
                .then(function (r) { return r.json(); })
                .then(function (json) {
                    state.meta = json.meta || null;
                    renderSeats(json.data || []);
                    updatePagination();
                })
                .catch(function () {
                    listEl.innerHTML = '<div class="ukpr-seats-empty">Failed to load seats — please try again.</div>';
                })
                .finally(function () { setLoading(false); });
        }

        function resetAndFetch() { state.page = 1; fetchSeats(); }

        filterToggle.addEventListener('click', function () {
            var open = !filtersPanel.hidden;
            filtersPanel.hidden = open;
            filterToggle.textContent = open ? 'Filters ▾' : 'Filters ▴';
        });

        searchInput.addEventListener('input', function () {
            clearTimeout(state.searchTimer);
            state.searchTimer = setTimeout(resetAndFetch, 380);
        });

        /* Sync model from chart widget via custom event */
        document.addEventListener('ukpr:results-history-model-change', function (e) {
            if (e.detail && e.detail.model) {
                currentModel = e.detail.model;
                resetAndFetch();
            }
        });

        filterIncumbent.addEventListener('change', resetAndFetch);
        filterWinner.addEventListener('change', resetAndFetch);
        filterRegion.addEventListener('change', resetAndFetch);
        filterFlipped.addEventListener('change', resetAndFetch);

        prevBtn.addEventListener('click', function () {
            if (state.page <= 1) return;
            state.page--; fetchSeats();
        });
        nextBtn.addEventListener('click', function () {
            if (state.meta && state.page >= state.meta.last_page) return;
            state.page++; fetchSeats();
        });

        fetchSeats();
    }

    /* ================================================================
       DETAIL VIEW
    ================================================================ */

    function runDetail() {
        var modelSelect = detailView.querySelector('#ukpr-seats-model-detail');
        var titleEl     = root.querySelector('[data-seats-detail-title]');
        var barsEl      = root.querySelector('[data-seats-detail-bars]');
        var statusEl    = root.querySelector('[data-seats-detail-status]');
        var canvas      = root.querySelector('[data-seats-canvas]');
        var chartSection = root.querySelector('[data-seats-detail-view] .ukpr-seats-chart-section');
        var loadingBar  = root.querySelector('[data-seats-loading]');

        var partyLogoFallbacks = {
            con:    'https://assets.pollingreport.uk/public/images/ukpr-con-temp.jpg',
            lab:    'https://assets.pollingreport.uk/public/images/ukpr-lab-temp.jpg',
            lib:    'https://assets.pollingreport.uk/public/images/ukpr-lib-temp.jpg',
            snp:    'https://assets.pollingreport.uk/public/images/ukpr-snp-temp.jpg',
            green:  'https://assets.pollingreport.uk/public/images/ukpr-green-temp.jpg',
            reform: 'https://assets.pollingreport.uk/public/images/ukpr-reform-temp.jpg',
            plaid:  'https://assets.pollingreport.uk/public/images/ukpr-plaid-temp.jpg',
            other:  ''
        };

        var state = { seatId: null, chart: null, barsReady: false, barTimers: {}, chartIntroDone: false };

        function setLoading(on) { loadingBar.hidden = !on; }

        function escHtml(s) {
            return String(s || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
        }

        function setStatus(msg) { statusEl.textContent = msg; }

        function setStatusHtml(html) { statusEl.innerHTML = html; }

        function toTitleCase(s) {
            return (s || '').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
        }

        function partySpan(name, slug) {
            return '<span class="ukpr-seats-status-party party-' + escHtml(String(slug || '').toLowerCase()) + '">' + escHtml(name) + '</span>';
        }

        function lower(v) {
            return String(v || '').toLowerCase();
        }

        function getSeatMp(seat) {
            return seat.mp || (seat.constituency && seat.constituency.mp) || null;
        }

        function getCandidatesByParty(seat) {
            var list = [];
            if (Array.isArray(seat.candidates)) {
                list = seat.candidates;
            } else if (seat.constituency && Array.isArray(seat.constituency.candidates)) {
                list = seat.constituency.candidates;
            }

            var byParty = {};
            list.forEach(function (candidate) {
                var stub = lower(
                    (candidate.party && (candidate.party.stub || candidate.party.slug))
                    || candidate.party_slug
                );
                if (!stub) return;
                byParty[stub] = candidate;
            });
            return byParty;
        }

        function getVoteValue(seat, key) {
            if (seat && seat.votes && seat.votes[key] && seat.votes[key].prediction != null) {
                return Number(seat.votes[key].prediction || 0);
            }
            return Number(seat[key] || 0);
        }

        function getDisplayName(seat, key, mp, mpParty, candidatesByParty) {
            var incumbent = lower(seat.incumbent);
            var candidate = candidatesByParty[key];

            function withParty(fullName, partyKey, explicitPartyName) {
                var partyName = explicitPartyName || partyNames[partyKey] || toTitleCase(partyKey);
                return String(fullName || '').trim() + ' - ' + partyName;
            }

            if (mp && mpParty === key && !mp.standing_down) {
                return withParty(
                    mp.full_name || partyNames[key] || toTitleCase(key),
                    key,
                    mp.party && mp.party.name
                );
            }

            if (candidate && candidate.full_name) {
                return withParty(
                    candidate.full_name,
                    key,
                    candidate.party && candidate.party.name
                );
            }

            if (key === 'other' && candidatesByParty.ind && candidatesByParty.ind.full_name) {
                return withParty(
                    candidatesByParty.ind.full_name,
                    'ind',
                    candidatesByParty.ind.party && candidatesByParty.ind.party.name
                );
            }

            if (key === incumbent && mp && !mp.standing_down && mpParty === incumbent) {
                return withParty(
                    mp.full_name || partyNames[key] || toTitleCase(key),
                    key,
                    mp.party && mp.party.name
                );
            }

            return partyNames[key] || toTitleCase(key);
        }

        function getDisplayImage(seat, key, mp, mpParty, candidatesByParty) {
            var incumbent = lower(seat.incumbent);
            var candidate = candidatesByParty[key];

            if (mp && mpParty === key && !mp.standing_down && mp.thumbnail_url) {
                return mp.thumbnail_url;
            }

            if (candidate && candidate.thumbnail_url) {
                return candidate.thumbnail_url;
            }

            if (key === 'other' && candidatesByParty.ind && candidatesByParty.ind.thumbnail_url) {
                return candidatesByParty.ind.thumbnail_url;
            }

            if (key === incumbent && mp && !mp.standing_down && mpParty === incumbent && mp.thumbnail_url) {
                return mp.thumbnail_url;
            }

            return partyLogoFallbacks[key] || '';
        }

        function setDetailHeader(seat) {
            titleEl.textContent = seat.constituency_name || seatCode;
            var winnerSlug    = String(seat.winner || '').toLowerCase();
            var incumbentSlug = String(seat.incumbent || '').toLowerCase();
            var winnerName    = partyNames[winnerSlug]    || toTitleCase(winnerSlug);
            var incumbentName = partyNames[incumbentSlug] || toTitleCase(incumbentSlug);
            var txt = partySpan(winnerName, winnerSlug);
            if (seat.flipped && incumbentName) {
                txt += ' Gain from ' + partySpan(incumbentName, incumbentSlug);
            } else {
                txt += ' Hold';
            }
            if (seat.majority != null) {
                txt += ' — majority ' + Number(seat.majority).toFixed(1) + '%';
            }
            setStatusHtml(txt);
        }

        function fmtDate(str) {
            var date = new Date(str);
            if (!Number.isFinite(date.getTime())) return String(str || '');
            return date.toLocaleDateString('en-GB', {day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC'});
        }

        function dateMillis(label) {
            return Date.parse(String(label).slice(0, 10) + 'T00:00:00Z');
        }

        function monthYear(ms) {
            return new Date(Number(ms)).toLocaleDateString('en-GB', {month: 'short', year: 'numeric', timeZone: 'UTC'});
        }

        /* ---- bar chart ---- */
        function ensureBarMarkup() {
            if (barsEl.dataset.initialized === 'true') return;

            barsEl.innerHTML = partyOrder.map(function (key) {
                return '<div class="ukpr-seat-bar-row" data-seat-bar-row="' + escHtml(key) + '">'
                    + '<div class="ukpr-seat-bar-line">'
                    + '<div class="ukpr-seat-bar-avatar">'
                    + '<img class="ukpr-seat-bar-avatar-img" alt="">'
                    + '</div>'
                    + '<div class="ukpr-seat-bar-track">'
                    + '<div class="ukpr-seat-bar-fill" style="width:0%;background:' + escHtml(partyColors[key] || '#9E9E9E') + '"></div>'
                    + '</div>'
                    + '</div>'
                    + '<div class="ukpr-seat-bar-labels">'
                    + '<span class="ukpr-seat-bar-party">' + escHtml(partyNames[key] || key) + '</span>'
                    + '<strong class="ukpr-seat-bar-pct">0.0%</strong>'
                    + '</div>'
                    + '</div>';
            }).join('');

            barsEl.dataset.initialized = 'true';
        }

        function getOrderedRows(seat) {
            var mp = getSeatMp(seat) || {};
            var mpParty = lower((mp.party && mp.party.stub) || mp.party_slug);
            var candidatesByParty = getCandidatesByParty(seat);

            var rows = partyOrder.map(function (key) {
                return {
                    key: key,
                    label: partyNames[key] || key,
                    value: getVoteValue(seat, key),
                    displayName: getDisplayName(seat, key, mp, mpParty, candidatesByParty),
                    imageUrl: getDisplayImage(seat, key, mp, mpParty, candidatesByParty)
                };
            });
            var sorted = rows.slice().sort(function (a, b) { return b.value - a.value; });
            var orderMap = {};
            sorted.forEach(function (row, index) {
                orderMap[row.key] = index;
            });
            rows.forEach(function (row) {
                row.order = orderMap[row.key];
            });
            return rows;
        }

        function renderBars(seat) {
            ensureBarMarkup();

            var rows = getOrderedRows(seat);
            var topValue = rows.reduce(function (maxVal, row) {
                return Math.max(maxVal, Number(row.value || 0));
            }, 0);
            var scaleMax = Math.max(10, topValue + 10);
            var firstRender = !state.barsReady;

            rows.forEach(function (row) {
                var rowEl = barsEl.querySelector('[data-seat-bar-row="' + row.key + '"]');
                if (!rowEl) return;

                var labelsEl = rowEl.querySelector('.ukpr-seat-bar-labels');
                var nameEl   = rowEl.querySelector('.ukpr-seat-bar-party');
                var pctEl    = rowEl.querySelector('.ukpr-seat-bar-pct');
                var fillEl   = rowEl.querySelector('.ukpr-seat-bar-fill');
                var trackEl  = rowEl.querySelector('.ukpr-seat-bar-track');
                var avatarEl = rowEl.querySelector('.ukpr-seat-bar-avatar');
                var avatarImg = rowEl.querySelector('.ukpr-seat-bar-avatar-img');
                var widthPct = (Number(row.value || 0) / scaleMax) * 100;
                var width    = Math.max(0, Math.min(100, widthPct));
                var hidden   = row.value <= 0;
                var fallbackLogo = partyLogoFallbacks[row.key] || '';

                clearTimeout(state.barTimers[row.key]);
                rowEl.style.order = row.order;
                rowEl.style.setProperty('--seat-party-color', partyColors[row.key] || '#9E9E9E');

                fillEl.style.transition = firstRender
                    ? 'width 1.5s ease, background-color 0.2s ease'
                    : 'width 0.5s ease, background-color 0.2s ease';

                if (state.barsReady) {
                    rowEl.classList.add('is-updating');
                }

                state.barTimers[row.key] = setTimeout(function () {
                    rowEl.classList.toggle('is-hidden', hidden);
                    nameEl.textContent = row.displayName;
                    pctEl.textContent = row.value.toFixed(1) + '%';
                    fillEl.style.background = partyColors[row.key] || '#9E9E9E';

                    if (firstRender) {
                        fillEl.style.width = '0%';
                        trackEl.classList.remove('is-visible');
                        requestAnimationFrame(function () {
                            trackEl.classList.add('is-visible');
                            requestAnimationFrame(function () {
                                fillEl.style.width = width.toFixed(3) + '%';
                            });
                        });
                    } else {
                        trackEl.classList.add('is-visible');
                        fillEl.style.width = width.toFixed(3) + '%';
                    }

                    avatarEl.classList.remove('is-empty');
                    avatarImg.hidden = false;
                    avatarImg.onerror = function () {
                        this.onerror = null;
                        if (fallbackLogo) {
                            this.src = fallbackLogo;
                            return;
                        }
                        this.hidden = true;
                        avatarEl.classList.add('is-empty');
                    };

                    if (row.imageUrl) {
                        avatarImg.src = row.imageUrl;
                        avatarImg.alt = row.displayName;
                    } else if (fallbackLogo) {
                        avatarImg.src = fallbackLogo;
                        avatarImg.alt = partyNames[row.key] || row.key;
                    } else {
                        avatarImg.hidden = true;
                        avatarEl.classList.add('is-empty');
                    }

                    setTimeout(function () {
                        if (!state.barsReady) return;
                        rowEl.classList.remove('is-updating');
                    }, 180);
                }, state.barsReady ? 40 : 0);

                if (!state.barsReady) {
                    rowEl.classList.toggle('is-hidden', hidden);
                    nameEl.textContent = row.displayName;
                    pctEl.textContent = row.value.toFixed(1) + '%';
                    fillEl.style.width = '0%';
                }
            });

            state.barsReady = true;
        }

        /* ---- time-series chart ---- */
        function buildChartDatasets(data) {
            return (data.datasets || [])
                .filter(function (ds) { return ds.data.some(function (v) { return Number(v) > 0; }); })
                .sort(function (a, b) {
                    return (partyOrder.indexOf(a.label) + 1 || 99) - (partyOrder.indexOf(b.label) + 1 || 99);
                })
                .map(function (ds) {
                    var color = partyColors[ds.label] || '#9E9E9E';
                    return {
                        label:           partyNames[ds.label] || ds.label,
                        data:            ds.data,
                        borderColor:     color,
                        backgroundColor: color,
                        borderWidth:     2.5,
                        pointRadius:     0,
                        tension:         0.25
                    };
                });
        }

        function renderChart(data) {
            var cssVars   = getComputedStyle(document.documentElement);
            var gridColor = cssVars.getPropertyValue('--color-border').trim()         || 'rgba(0,0,0,0.08)';
            var tickColor = cssVars.getPropertyValue('--color-secondary-text').trim() || 'rgba(0,0,0,0.55)';
            var fontFam   = cssVars.getPropertyValue('--font-data').trim()            || 'monospace';

            var labels = data.labels || [];
            var times = labels.map(dateMillis);
            var datasets = buildChartDatasets(data).map(function (ds) {
                ds.data = ds.data.map(function (value, i) {
                    return {x: times[i], y: value == null ? null : Number(value)};
                }).filter(function (point) { return Number.isFinite(point.x); });
                return ds;
            });
            var validTimes = times.filter(Number.isFinite).sort(function (a, b) { return a - b; });
            if (chartSection && validTimes.length) {
                var caption = chartSection.querySelector('[data-seats-history-range]');
                if (!caption) {
                    caption = document.createElement('p');
                    caption.setAttribute('data-seats-history-range', '');
                    caption.className = 'ukpr-seats-chart-range';
                    chartSection.querySelector('.ukpr-seats-chart-heading').insertAdjacentElement('afterend', caption);
                }
                caption.textContent = fmtDate(validTimes[0]) + ' – ' + fmtDate(validTimes[validTimes.length - 1]) + ' · Projection snapshot dates';
            }

            if (state.chart) {
                state.chart.data.labels = [];
                state.chart.data.datasets = datasets;
                state.chart.options.animation.duration = 1500;
                state.chart.options.animation.easing = 'easeInOutCubic';
                state.chart.update();
                return;
            }

            state.chart = new Chart(canvas, {
                type: 'line',
                data: { labels: [], datasets: datasets },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    animation: { duration: 1500, easing: 'easeInOutCubic' },
                    plugins: {
                        legend: {
                            position: 'bottom',
                            labels: {
                                color: tickColor,
                                font: { family: fontFam, size: 11 },
                                boxWidth: 12,
                                padding: 16
                            }
                        },
                        tooltip: {
                            intersect: false,
                            mode: 'index',
                            callbacks: {
                                title: function (items) { return items.length ? fmtDate(items[0].parsed.x) : ''; },
                                label: function (item) {
                                    return item.dataset.label + ': ' + Number(item.parsed.y).toFixed(1) + '%';
                                }
                            }
                        }
                    },
                    scales: {
                        x: {
                            type: 'linear',
                            grid: { color: gridColor },
                            ticks: {
                                color: tickColor,
                                font: { family: fontFam, size: 11 },
                                maxTicksLimit: 9,
                                callback: function (value) { return monthYear(value); }
                            }
                        },
                        y: {
                            grid: { color: gridColor },
                            ticks: {
                                color: tickColor,
                                font: { family: fontFam, size: 11 },
                                callback: function (value) { return value + '%'; }
                            }
                        }
                    },
                    interaction: { intersect: false, mode: 'nearest', axis: 'x' }
                }
            });

            if (!state.chartIntroDone && chartSection) {
                requestAnimationFrame(function () {
                    chartSection.classList.add('is-ready');
                });
                state.chartIntroDone = true;
            }
        }

        /* ---- data loading ---- */
        function loadSeat() {
            setLoading(true);

            /* First resolve the internal UUID from the constituency code */
            var searchUrl = API_BASE + '/seats?paginate=true&per_page=100&verbose=true'
                + '&model=' + encodeURIComponent(modelSelect.value)
                + '&search=' + encodeURIComponent(seatCode);

            fetch(searchUrl)
                .then(function (r) { return r.json(); })
                .then(function (json) {
                    var items = (json && Array.isArray(json.data)) ? json.data : [];
                    var seatSummary = items.find(function (s) {
                        return String(s.constituency_code || '').toUpperCase() === seatCode;
                    });
                    if (!seatSummary) throw new Error('Seat not found');
                    state.seatId = seatSummary.id;
                    return fetch(API_BASE + '/seats/' + encodeURIComponent(state.seatId) + '?model=' + encodeURIComponent(modelSelect.value))
                        .then(function (r) { return r.json(); })
                        .then(function (payload) {
                            return (payload && payload.data) ? payload.data : seatSummary;
                        })
                        .catch(function () {
                            return seatSummary;
                        });
                })
                .then(function (seat) {
                    setDetailHeader(seat);
                    document.dispatchEvent(new CustomEvent('ukpr:seat-detail-loaded', {
                        detail: { seat: seat, model: modelSelect.value }
                    }));
                    renderBars(seat);
                    return loadHistory();
                })
                .catch(function () {
                    setStatus('Seat not found — ' + seatCode);
                })
                .finally(function () { setLoading(false); });
        }

        function loadHistory() {
            return new Promise(function (resolve) {
                function doFetch() {
                    fetch(API_BASE + '/seats/' + encodeURIComponent(state.seatId) + '/history?model=' + encodeURIComponent(modelSelect.value))
                        .then(function (r) { return r.json(); })
                        .then(function (data) { renderChart(data); resolve(); })
                        .catch(function () { setStatus('Could not load history data.'); resolve(); });
                }
                if (typeof Chart !== 'undefined') {
                    doFetch();
                } else {
                    var s = document.createElement('script');
                    s.src = CHARTJS_CDN;
                    s.onload = doFetch;
                    document.head.appendChild(s);
                }
            });
        }

        modelSelect.addEventListener('change', loadSeat);

        loadSeat();
    }
})();
